import { memo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Ban } from 'lucide-react';
import type { TicketApproval } from '@/entities/ticket';
import { PendingIcon, RejectedIcon, ResolvedIcon } from '@/shared/config';
import { cn } from '@/shared/lib/cn';
import styles from './ApprovalRow.module.css';

const STATUS_LABELS: Record<TicketApproval['status'], string> = {
  pending: 'ожидает',
  approved: 'согласовано',
  rejected: 'отклонено',
  cancelled: 'снято',
};

const STATUS_ICONS: Record<TicketApproval['status'], typeof Ban> = {
  pending: PendingIcon,
  approved: ResolvedIcon,
  rejected: RejectedIcon,
  cancelled: Ban,
};

export interface ApprovalRowProps {
  approval: TicketApproval;
  name: string;
  profileHref?: string | null;
  subject?: string | null;
  actions?: ReactNode;
  children?: ReactNode;
  as?: 'div' | 'li';
}

const ApprovalRowBase: React.FC<ApprovalRowProps> = ({
  approval: a,
  name,
  profileHref,
  subject,
  actions,
  children,
  as: Tag = 'div',
}) => {
  const Icon = STATUS_ICONS[a.status];
  return (
    <Tag className={styles.row}>
      <span className={cn(styles.icon, styles[`icon-${a.status}`])} aria-hidden>
        <Icon size={16} />
      </span>
      <p className={styles.head}>
        {profileHref ? (
          <Link to={profileHref} className={cn(styles.name, styles.nameLink)} title={name}>
            {name}
          </Link>
        ) : (
          <strong className={styles.name} title={name}>
            {name}
          </strong>
        )}
        <span className={cn(styles.status, styles[`status-${a.status}`])}>
          {STATUS_LABELS[a.status]}
        </span>
      </p>
      {(a.decidedAt || actions) && (
        <div className={styles.aside}>
          {a.decidedAt && (
            <time className={styles.date} dateTime={a.decidedAt}>
              {new Date(a.decidedAt).toLocaleDateString('ru', { day: 'numeric', month: 'short' })}
            </time>
          )}
          {actions}
        </div>
      )}
      {subject && <p className={cn(styles.wide, styles.subject)}>{subject}</p>}
      {a.status !== 'pending' && a.decisionComment && (
        <p className={cn(styles.wide, styles.comment)}>«{a.decisionComment}»</p>
      )}
      {children && <div className={cn(styles.wide, styles.extra)}>{children}</div>}
    </Tag>
  );
};

export const ApprovalRow = memo(ApprovalRowBase);
